import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import DateHelper from '../../../../helpers/date-helper';
import EmployeeHelper from '../../../../helpers/employee-helper';
import { StringHelper } from '../../../../helpers/string-helper';
import { SimpleLoggedAction } from '../../../../model/simple-logged-action';
import {
  ImmoBillingDetailResponse,
  SubProjectControllerRequestService,
} from '../../../../services/api/sub-project-controller-request.service';
import { SubProjectTourInfo } from '../../../projects/helpers/ip-board-sub-project-tour-selector/helper/model/sub-project-tour-info';
import { SubProjectTourInfoControllerRequestService } from '../../../projects/helpers/ip-board-sub-project-tour-selector/service/sub-project-tour-info-controller-request.service';
import { ImmoBillingDetailSubProject } from './model/immo-billing-detail-sub-project';
import Company from '../../../../model/company/company';
import { GeneralHelper } from '../../../../helpers/general-helper';
import ProductAsset from 'src/app/model/product-asset';
import ProjectHelper from 'src/app/helpers/project-helper';
import { NotExecutedHistoryItem } from 'src/app/model/history-item/not-executed-history-item';
import { Router } from '@angular/router';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import Employee from 'src/app/model/product/employee';
import { User } from 'src/app/model/user';
import { ModalService } from 'src/app/services/modal.service';
import { AuthService } from 'src/app/services/auth.service';
import { FileHelper } from '../../../../helpers/file-helper';

@Component({
  selector: 'immo-billing-detail',
  templateUrl: './immo-billing-detail.component.html',
  styleUrls: ['./immo-billing-detail.component.scss'],
})
export class ImmoBillingDetailComponent implements OnInit {
  @Input()
  subProjectId: number;

  @Output()
  billingChecked = new EventEmitter<SimpleLoggedAction>();

  loading: boolean;
  detail: ImmoBillingDetailSubProject;
  baseProject: ImmoBillingDetailSubProject;
  billingReadyLoading: boolean;

  executionHeader: string;
  placerDisplay: string;
  placer: Employee;
  notExecutedHistoryItem: NotExecutedHistoryItem;

  involvedCompanies: Company[];

  latestExecutionAssets: ProductAsset[];
  minDate: Date;
  isImage = FileHelper.isImage;

  savingLines = false;

  constructor(
    private subProjectControllerRequestService: SubProjectControllerRequestService,
    private subProjectTourInfoControllerRequestService: SubProjectTourInfoControllerRequestService,
    private router: Router,
    private dialogRef: MatDialogRef<ImmoBillingDetailComponent>,
    private modalService: ModalService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.fetchDetail();
    this.fetchPlacer();
  }

  private calculateInvolvedCompanies(): void {
    const project = this.detail.project;
    if (project == null || project.projectContacts == null) {
      return null;
    }
    let toReturn = project.projectContacts
      .filter((pc) => pc.company != null)
      .map((pc) => pc.company);
    this.involvedCompanies = toReturn.filter(
      (c, i) => toReturn.findIndex((c1) => c.id === c1.id) === i
    );
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }

  fetchDetail() {
    this.loading = true;

    this.subProjectControllerRequestService
      .getImmoBillingDetail(this.subProjectId)
      .then((res) => {
        this.detail = res.subProject;
        this.detail.type = this.detail.expectedType;
        this.baseProject = res.baseProject;
        this.latestExecutionAssets = res.latestExecutionAssets;
        if (this.baseProject != null) {
          this.minDate = new Date();
        }
        this.calculateInvolvedCompanies();
        this.processStatusDependent(res);
      })
      .finally(() => (this.loading = false));
  }

  private processStatusDependent(res: ImmoBillingDetailResponse): void {
    if (ProjectHelper.NOT_EXECUTED_STATUSES.includes(this.detail.status)) {
      this.executionHeader = 'Niet uitgevoerd';
      this.notExecutedHistoryItem = res.notExecutedHistoryItem;
    } else {
      this.executionHeader = 'Uitgevoerd';
    }
  }

  fetchPlacer() {
    this.subProjectTourInfoControllerRequestService
      .subProjectTourInfo(this.subProjectId)
      .then((subProjectTourInfo: SubProjectTourInfo) => {
        if (subProjectTourInfo != null) {
          this.placer = subProjectTourInfo.placer;
          this.placerDisplay = StringHelper.join(
            [
              subProjectTourInfo.date != null
                ? DateHelper.formatDate(subProjectTourInfo.date, 'dd/MM/yyyy')
                : 'datum',
              subProjectTourInfo.placer != null
                ? EmployeeHelper.getName(subProjectTourInfo.placer)
                : 'Installateur',
            ],
            ' - '
          );
        }
      });
  }

  savePriceUpdate(detail: ImmoBillingDetailSubProject) {
    this.detail = detail;
  }

  savePriceUpdateLines() {
    this.savingLines = true;
    this.saveBillingInvoiceDetails(this.detail);
  }

  saveBillingInvoiceDetails = (detail: ImmoBillingDetailSubProject) => {
    const saveBillingInvoiceDtos = detail.projectInvoices.map((invoice) => {
      return {
        projectInvoiceId: invoice.id,
        onlyChargePriceLines: invoice.onlyChargePriceLines,
        priceLineDtoList: invoice.priceLines.map((line) => {
          return {
            id: line.id,
            text: line.text,
            price: line.price,
          };
        }),
      };
    });
    this.subProjectControllerRequestService
      .saveImmoBillingInvoices(saveBillingInvoiceDtos)
      .finally(() => (this.savingLines = false));
  };

  onCheckBillingReady(value: boolean): void {
    this.billingReadyLoading = true;

    this.subProjectControllerRequestService
      .markBillingReady(this.subProjectId, value)
      .then((res) => {
        this.detail.billingReady = res;
        this.billingChecked.emit(res);
      })
      .finally(() => {
        this.billingReadyLoading = false;
      });
  }

  openSubProject(): void {
    this.router.navigate(['/job', this.detail.project.id, this.detail.id]);
    this.dialogRef.close();
  }

  impersonateUser(user: User): void {
    if (user.id != null) {
      const userName = StringHelper.join([user.firstName, user.lastName], ' ');
      this.modalService.confirmDialog(
        `${userName} nabootsen`,
        `Bent u zeker dat u ${userName} wilt nabootsen?`,
        (b: boolean) => {
          if (b) {
            this.closeDialog();
            this.authService.impersonateUser({ id: user.id });
          }
        }
      );
    }
  }

  closeDialog() {
    this.dialogRef.close(false);
  }
}
